import { useSyncExternalStore } from 'react';
import { UserRole } from '@blagodar/shared';

/**
 * ПЕРЕКЛЮЧАТЕЛЬ НОВОГО ВИДА (правило 295).
 *
 * Новый вид дизайнера включается не выкладкой, а переключателем: выключил —
 * сайт выглядит как раньше, без новой версии. Владелец (05.11.2026): по
 * умолчанию он у руководства, у остальных сотрудников — прежний вид, пока
 * сами не включат в меню учётки.
 *
 * Выбор человека хранится на устройстве: удобство, а не данные (правило 212).
 * Не сделан выбор — решает роль.
 */
export type DesignChoice = 'warm' | 'classic';

const KEY = 'blagodar.design';
const WARM_THEME_COLOR = '#E7E5E2';
const CLASSIC_THEME_COLOR = '#1e3a8a';

/** Кому новый вид включён по умолчанию */
export const WARM_BY_DEFAULT: readonly string[] = [UserRole.ADMIN, UserRole.MANAGER];

let roles: readonly string[] = [];
const listeners = new Set<() => void>();
// Память устройства недоступна (приватное окно) — выбор живёт до перезагрузки
let memoryChoice: DesignChoice | null = null;

export function storedDesignChoice(): DesignChoice | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'warm' || v === 'classic' ? v : null;
  } catch {
    return memoryChoice;
  }
}

/** Новый ли вид при этих ролях и этом выборе на устройстве */
export function warmFor(userRoles: readonly string[], choice: DesignChoice | null): boolean {
  if (choice) return choice === 'warm';
  return userRoles.some((r) => WARM_BY_DEFAULT.includes(r));
}

export function isWarmDesign(): boolean {
  return warmFor(roles, storedDesignChoice());
}

function apply(): void {
  const warm = isWarmDesign();
  const root = document.documentElement;
  if (warm) root.dataset.design = 'warm';
  else delete root.dataset.design;
  // Цвет полосы браузера на телефоне — под фон экрана
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', warm ? WARM_THEME_COLOR : CLASSIC_THEME_COLOR);
  for (const l of [...listeners]) l();
}

/** Вошёл или вышел человек: вид по умолчанию зависит от его ролей */
export function setDesignRoles(next: readonly string[]): void {
  const same = next.length === roles.length && next.every((r, i) => r === roles[i]);
  roles = [...next];
  if (!same) apply();
}

/** Человек выбрал вид сам; null — вернуть «как положено по роли» */
export function setDesignChoice(choice: DesignChoice | null): void {
  memoryChoice = choice;
  try {
    if (choice) localStorage.setItem(KEY, choice);
    else localStorage.removeItem(KEY);
  } catch {
    // Память недоступна — вид меняется до перезагрузки
  }
  apply();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useWarmDesign(): boolean {
  return useSyncExternalStore(subscribe, isWarmDesign, isWarmDesign);
}

// Первый кадр — уже в нужном виде: выбор с устройства известен до входа
apply();
